import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface Flag { id: number; key: string; label: string; description: string; enabled: boolean; rollout_percentage: number; updated_at: string; }

const FeatureFlags: React.FC = () => {
    const { api } = useInternalApi();
    const [flags, setFlags] = useState<Flag[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ key: '', label: '', description: '', enabled: false, rollout_percentage: 100 });

    useEffect(() => { document.title = 'Feature Flags & Rollouts - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('platform/admin/feature-flags/');
            if (res.data?.status === 'success') setFlags(res.data.data || []);
        } catch { setFlags([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleEnabled = async (f: Flag) => {
        try {
            await api.patch(`platform/admin/feature-flags/${f.id}/`, { enabled: !f.enabled });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update flag.' });
        }
    };

    const updateRollout = async (f: Flag, pct: number) => {
        try {
            await api.patch(`platform/admin/feature-flags/${f.id}/`, { rollout_percentage: pct });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update rollout.' });
        }
    };

    const deleteFlag = async (f: Flag) => {
        const confirm = await Swal.fire({ title: `Delete "${f.label}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`platform/admin/feature-flags/${f.id}/`);
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not delete flag.' });
        }
    };

    const submitCreate = async () => {
        if (!form.key.trim() || !form.label.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Key and label are required.' });
            return;
        }
        try {
            const res = await api.post('platform/admin/feature-flags/', form);
            if (res.data?.status === 'success') {
                setShowCreate(false);
                setForm({ key: '', label: '', description: '', enabled: false, rollout_percentage: 100 });
                load();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create flag.' });
        }
    };

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faToggleOn} className="me-2 text-primary" />Feature Flags & Rollouts</h2>
                    <p className="text-muted mb-0">Toggle features platform-wide, or stage a percentage rollout</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Flag</button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : flags.length === 0 ? (
                        <div className="text-center py-5 text-muted">No feature flags yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Flag</th><th>Description</th><th>Rollout %</th><th>Status</th><th>Updated</th><th></th></tr></thead>
                            <tbody>
                                {flags.map(f => (
                                    <tr key={f.id}>
                                        <td><strong>{f.label}</strong><div className="small text-muted"><code>{f.key}</code></div></td>
                                        <td className="small">{f.description || '—'}</td>
                                        <td style={{ width: 140 }}>
                                            <input type="number" className="form-control form-control-sm" min={0} max={100} value={f.rollout_percentage}
                                                onChange={(e) => updateRollout(f, Math.max(0, Math.min(100, Number(e.target.value))))} />
                                        </td>
                                        <td>
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox" checked={f.enabled} onChange={() => toggleEnabled(f)} />
                                            </div>
                                        </td>
                                        <td className="small">{new Date(f.updated_at).toLocaleString()}</td>
                                        <td><button className="btn btn-sm btn-outline-danger" onClick={() => deleteFlag(f)}><FontAwesomeIcon icon={faTrash} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Feature Flag</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Key</label>
                                    <input className="form-control" placeholder="e.g. telehealth-v2" value={form.key} onChange={(e) => setForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Label</label>
                                    <input className="form-control" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6 d-flex align-items-center">
                                        <div className="form-check form-switch">
                                            <input className="form-check-input" type="checkbox" checked={form.enabled} onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked }))} />
                                            <label className="form-check-label">Enabled</label>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Rollout %</label>
                                        <input type="number" className="form-control" min={0} max={100} value={form.rollout_percentage} onChange={(e) => setForm(f => ({ ...f, rollout_percentage: Number(e.target.value) }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default FeatureFlags;
